import { Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { environment } from 'environments/environment';
import { TeamsM365ConnectService } from '@app/shared/common/auth/teams-m365-connect.service';

@Component({
    selector: 'vf-admin-consent-modal',
    templateUrl: './admin-consent-modal.component.html'
})
export class AdminConsentModalComponent implements OnChanges, OnDestroy {

    @Input() isOpen = false;
    @Input() isInTeams = false;
    @Output() closed = new EventEmitter<void>();
    @Output() consentApproved = new EventEmitter<void>();

    constructor(private _ngZone: NgZone, private _teamsConnect: TeamsM365ConnectService) {}

    showEmailTemplate = false;
    adminConsentFaqOpen = false;
    adminConsentEmailBody = '';
    adminConsentEmailHtml = '';
    adminConsentMailtoLink = '';
    adminConsentUrl = '';

    private _consentPopupMessageHandler: ((event: MessageEvent) => void) | null = null;
    private _consentPopupBroadcastChannel: BroadcastChannel | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isOpen?.currentValue === true) {
            this._buildConsentData();
        }
        if (changes.isOpen?.currentValue === false) {
            this._reset();
        }
    }

    ngOnDestroy(): void {
        this._cleanupPopupListeners();
    }

    private _buildConsentData(): void {
        const clientId = AppConsts.O365QueryParams.clientId || environment.clientID;
        this.adminConsentUrl =
            `https://login.microsoftonline.com/organizations/v2.0/adminconsent` +
            `?client_id=${clientId}` +
            `&scope=${encodeURIComponent(AppConsts.fullScopes.join(' '))}` +
            `&redirect_uri=${encodeURIComponent(window.location.origin + AppConsts.adminConsentRedirectUri)}`;

        this.adminConsentEmailBody =
            `Hi there,\n\n` +
            `I'd like to start using Trovve, a project management app that integrates directly with Microsoft Teams. ` +
            `It needs admin consent before our team can connect.\n\n` +
            `You can review the requested permissions and grant consent here:\n${this.adminConsentUrl}\n\n` +
            `A few things worth noting:\n\n` +
            `- Trovve is hosted entirely on Microsoft Azure — no third-party servers\n` +
            `- All files stay in our Microsoft 365 tenant; Trovve never stores or accesses file content\n` +
            `- It uses Microsoft SSO, so there are no separate credentials to manage\n` +
            `- Data is encrypted with AES-256 at rest and SSL/TLS in transit\n` +
            `- Only metadata (task names, dates, status) is stored; never the content of files\n\n` +
            `More detail on their security and data practices: https://trovve.com/security/\n\n` +
            `Happy to answer any questions. Thank you!`;

        this.adminConsentEmailHtml =
            `Hi there,<br><br>` +
            `I'd like to start using Trovve, a project management app that integrates directly with Microsoft Teams. ` +
            `It needs admin consent before our team can connect.<br><br>` +
            `You can review the requested permissions and grant consent here:<br>` +
            `<a href="${this.adminConsentUrl}">Approve Trovve for Microsoft 365</a><br><br>` +
            `A few things worth noting:<br><br>` +
            `• Trovve is hosted entirely on Microsoft Azure — no third-party servers<br>` +
            `• All files stay in our Microsoft 365 tenant; Trovve never stores or accesses file content<br>` +
            `• It uses Microsoft SSO, so there are no separate credentials to manage<br>` +
            `• Data is encrypted with AES-256 at rest and SSL/TLS in transit<br>` +
            `• Only metadata (task names, dates, status) is stored; never the content of files<br><br>` +
            `More detail on their security and data practices: <a href="https://trovve.com/security/">trovve.com/security</a><br><br>` +
            `Happy to answer any questions. Thank you!`;

        const subject = encodeURIComponent('Request: Approve Trovve access to Microsoft 365');
        this.adminConsentMailtoLink = `https://outlook.office.com/mail/deeplink/compose?subject=${subject}&body=${encodeURIComponent(this.adminConsentEmailHtml)}`;
    }

    private _reset(): void {
        this.showEmailTemplate = false;
        this.adminConsentFaqOpen = false;
        this._cleanupPopupListeners();
    }

    private _cleanupPopupListeners(): void {
        if (this._consentPopupMessageHandler) {
            window.removeEventListener('message', this._consentPopupMessageHandler);
            this._consentPopupMessageHandler = null;
        }
        if (this._consentPopupBroadcastChannel) {
            this._consentPopupBroadcastChannel.close();
            this._consentPopupBroadcastChannel = null;
        }
    }

    close(): void {
        this._reset();
        this.closed.emit();
    }

    copyAdminConsentEmailText(): void {
        const htmlBlob = new Blob([this.adminConsentEmailHtml], { type: 'text/html' });
        const textBlob = new Blob([this.adminConsentEmailBody], { type: 'text/plain' });
        const clipboardItem = new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob });
        navigator.clipboard.write([clipboardItem]).then(() => {
            abp.notify.success('Message copied to clipboard!');
        }).catch(() => {
            navigator.clipboard.writeText(this.adminConsentEmailBody).then(() => {
                abp.notify.success('Message copied to clipboard!');
            }).catch(() => {
                abp.notify.warn('Could not copy automatically. Please select and copy the text manually.');
            });
        });
    }

    openAdminConsentUrl(): void {
        if (this.isInTeams) {
            this._openAdminConsentViaTeams();
        } else {
            this._openAdminConsentViaPopup();
        }
    }

    private _openAdminConsentViaTeams(): void {
        // Opens the popup via the own-domain /account/admin-consent-start
        // intermediary (Teams SDK requires the popup URL to be within the app's
        // own domain). openAdminConsentPopup resolves when the popup settles,
        // success OR failure: COOP on login.microsoftonline.com severs the
        // popup's channel to the Teams host, so Teams reports "CancelledByUser"
        // even after the admin actually approved. Emit consentApproved either
        // way and let the parent verify the real outcome against Entra
        // (CheckTeamsConsent) — it re-shows this modal if consent is missing.
        this._teamsConnect.openAdminConsentPopup().then(() => {
            this._ngZone.run(() => {
                this.close();
                this.consentApproved.emit();
            });
        });
    }

    private _openAdminConsentViaPopup(): void {
        let popup: Window | null = null;
        const handleResult = (data: any) => {
            if (data?.type === 'vf_admin_consent_success') {
                this._cleanupPopupListeners();
                this.close();
                this.consentApproved.emit();
            } else if (data?.type === 'vf_admin_consent_error') {
                this._cleanupPopupListeners();
                abp.notify.error(data.error || 'Admin consent failed. Please try again.');
            }
        };

        // Replace any listeners left over from a previous click before wiring new ones.
        this._cleanupPopupListeners();

        localStorage.setItem('vf_admin_consent_popup', 'true');
        popup = window.open(this.adminConsentUrl, 'adminConsentPopup', 'width=600,height=700');

        if (!popup) {
            localStorage.removeItem('vf_admin_consent_popup');
            abp.notify.warn('Popup was blocked. Please allow popups for this site and try again.');
            return;
        }

        this._consentPopupMessageHandler = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) { return; }
            // Re-enter Angular's zone so close() + consentApproved.emit() trigger
            // change detection on the parent component.
            this._ngZone.run(() => handleResult(event.data));
        };
        window.addEventListener('message', this._consentPopupMessageHandler);

        try {
            this._consentPopupBroadcastChannel = new BroadcastChannel('vf_admin_consent_channel');
            // BroadcastChannel is not patched by Zone.js, so its callbacks fire
            // outside Angular's zone. Without _ngZone.run() here, the modal stays
            // visible because Angular never re-evaluates [isOpen].
            this._consentPopupBroadcastChannel.onmessage = (event: MessageEvent) =>
                this._ngZone.run(() => handleResult(event.data));
        } catch (e) { /* BroadcastChannel not supported */ }

        // We deliberately do not poll popup.closed. Cross-Origin-Opener-Policy
        // performs a browsing-context swap once the popup navigates to
        // login.microsoftonline.com — after which popup.closed can read true while
        // the popup is still visibly open, which would tear down our listeners
        // before consent finishes. Listeners are cleaned up when (a) a consent
        // message arrives, (b) the modal closes, or (c) the component is destroyed.
    }
}
